import type { DemoDomain } from './demoCenter'

export type SolutionComponent = 'SKP' | 'Human Capability Framework' | 'Noetivra'

export const solutionScenarios = [
  {
    setting: 'A research institution',
    need: 'may need to discover relationships across thousands of papers.',
  },
  {
    setting: 'A school',
    need: 'may need better pathways for developing reasoning.',
  },
  {
    setting: 'A public institution',
    need: 'may need to connect policy, staff development, and reviewable decisions.',
  },
]

export type SolutionSetting = {
  id: string
  demoId: DemoDomain['id']
  setting: string
  tagline: string
  components: SolutionComponent[]
  link: string
  linkLabel: string
  /** What the fictional demo shows today. */
  demo: string
  /** Proposed production use. */
  paragraphs: string[]
  safeguard?: string
  applications: string[]
}

/** Seven settings, in canonical domain order. */
export const solutionSettings: SolutionSetting[] = [
  {
    id: 'government',
    demoId: 'government',
    setting: 'For government and public institutions',
    tagline: 'Make institutional knowledge usable and decisions reviewable.',
    components: ['SKP', 'Human Capability Framework', 'Noetivra'],
    link: '/government',
    linkLabel: 'Government in depth',
    demo: 'The Government demo separates a Citizen Workspace from an Administration Control Panel in the fictional Republic of Nordland, with support requests, decisions and area reporting.',
    paragraphs: [
      'In production, SKP could organize policies, program knowledge, role requirements and approved rules so staff find current guidance. The Human Capability Framework could guide pilots in public-sector development, and Noetivra could provide policy exploration, staff learning and authorized review interfaces.',
    ],
    safeguard:
      'Where a proposed application could affect a person’s access or standing, additional systems are required: validated measures, secure identity and case management, authorized human decisions, notice, independent review, appeals and correction.',
    applications: ['Policy knowledge systems', 'Civil-service learning', 'Program planning', 'Carefully evaluated civic development pilots'],
  },
  {
    id: 'scientific-research',
    demoId: 'research',
    setting: 'For scientific research',
    tagline: 'Connect ideas across disciplines and documents.',
    components: ['SKP', 'Noetivra'],
    link: '/semantic-knowledge',
    linkLabel: 'Explore SKP',
    demo: 'The Research demo uses a synthetic collection of four fictional studies. You can propose a relationship, review it against its passage and see approved knowledge appear in the map.',
    paragraphs: [
      'In production, SKP is intended to organize papers, concepts, methods, populations and findings in a knowledge graph. AI could propose entities and relationships from source material; approved vocabularies, validation rules and human reviewers would govern what becomes trusted knowledge. Research does not require CJI or NCJI.',
    ],
    applications: ['Literature discovery', 'Biomedical knowledge graphs', 'Research portals', 'Evidence mapping', 'Cross-disciplinary collaboration'],
  },
  {
    id: 'education',
    demoId: 'education',
    setting: 'For education',
    tagline: 'Build reasoning and self-direction over time.',
    components: ['Human Capability Framework', 'SKP', 'Noetivra'],
    link: '/education',
    linkLabel: 'Education in depth',
    demo: 'The Education demo follows fictional adult learners through a task, criterion-based feedback, practice and reassessment.',
    paragraphs: [
      'In production, CJI and NCJI research could inform learning activities on evaluating claims, considering consequences, planning, cooperation and resilience to manipulation. School-age and early-childhood uses are proposed research directions that would need age-appropriate methods.',
    ],
    applications: ['Reasoning curricula', 'Media literacy', 'Mentoring pathways', 'Longitudinal development pilots'],
  },
  {
    id: 'employers',
    demoId: 'talent',
    setting: 'For employers and workforce programs',
    tagline: 'Connect strengths to development and opportunity.',
    components: ['Human Capability Framework', 'SKP', 'Noetivra'],
    link: '/employers',
    linkLabel: 'Employers in depth',
    demo: 'The Talent demo compares two fictional work samples against the same role criteria and records human decisions separately from development findings.',
    paragraphs: [
      'In production, SKP could maintain a consistent language for skills and role requirements, and the framework could connect findings to practice and coaching. Human reviewers make hiring decisions, and guidance stays separate from consequential eligibility decisions.',
    ],
    applications: ['Structured interviews', 'Workforce development', 'Internal mobility', 'Mentoring'],
  },
  {
    id: 'leadership',
    demoId: 'leadership',
    setting: 'For leadership development',
    tagline: 'Prepare people for decisions with wider consequences.',
    components: ['Human Capability Framework', 'SKP', 'Noetivra'],
    link: '/leadership',
    linkLabel: 'Leadership in depth',
    demo: 'The Leadership demo is a coaching demonstration: a service-outage decision, coach feedback, a rehearsed briefing and a revisited decision.',
    paragraphs: [
      'Leadership is a reusable capability that can be configured within employer, government, education and community programs. In production, exercises could support executive education, crisis practice and succession development.',
    ],
    safeguard:
      'Any proposed qualification use would require validation for the role, an explanation of findings and a meaningful route to review and reassessment.',
    applications: ['Executive education', 'Crisis exercises', 'Succession development', 'Institutional leadership research'],
  },
  {
    id: 'civil-society',
    demoId: 'community',
    setting: 'For civil society',
    tagline: 'Strengthen local knowledge and constructive participation.',
    components: ['SKP', 'Human Capability Framework', 'Noetivra'],
    link: '/civil-society',
    linkLabel: 'Civil society in depth',
    demo: 'The Community demo plans a practical next step with a fictional participant and records a consent-based, purpose-limited partner referral.',
    paragraphs: [
      'In production, SKP could connect knowledge held in scattered reports and programs, and programs could focus on media literacy, cooperation and practical pathways to participation, with each organization controlling what it shares.',
    ],
    applications: ['Community knowledge hubs', 'Misinformation resilience education', 'Local mentoring', 'Opportunity mapping'],
  },
  {
    id: 'clinical',
    demoId: 'clinical',
    setting: 'For clinical review',
    tagline: 'Review the clinical picture alongside its supporting knowledge.',
    components: ['SKP', 'Noetivra'],
    link: '/noetivra#clinical',
    linkLabel: 'Noetivra Clinical',
    demo: 'The scoped Clinical demo uses one fictional patient and two visits to illustrate structured documentation, clarification, review and addenda.',
    paragraphs: [
      'In production, Noetivra Clinical could help clinicians view a patient’s account, history, structured assessments and observations alongside approved references in SKP, keeping patient reports, measurements, observations and interpretations distinct. Clinical review does not require civic judgment assessment.',
    ],
    safeguard:
      'The clinician retains responsibility for assessment and diagnosis. Any diagnostic or sensor-based use would require clinical validation, and patient records would remain in controlled stores.',
    applications: ['Session review', 'Clinical reference lookup', 'Visit-to-visit comparison', 'Note preparation for clinician approval'],
  },
]

/** How a SyNeural engagement progresses (not a universal assessment cycle). */
export const solutionCycle = [
  'Connect knowledge',
  'Define the problem',
  'Develop capability',
  'Support action',
  'Examine outcomes',
  'Improve',
]
